import { ReferentialKey } from "api/referential";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { RootState } from "app/store";
import { selectReferentialDataByKey } from "features/referentials/selectors/selectReferentialDataByKey";
import { useCallback, useEffect, useMemo, useState } from "react";
import { initProfilingConfig } from "../slices/genericProfilingSlice";
import { ProfilingMode } from "../slices/types/enums";
import { IconPossibleAnswer, InputAnswer, PossibleAnswer } from "../types/interfaces";
import { useGenericProfiling } from "./useGenericProfiling";

import { toDayInput } from 'core/utils/dates';
// Fonction utilitaire pour obtenir la date de demain au format YYYY-MM-DD
const getTomorrowDate = (): string => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return toDayInput(tomorrow);
};

export const useBorrowerProfiling = (upsertBorrowerProject: (projectType: string, insuranceProject: string, projectStartDate: string) => void) => {
  const dispatch = useAppDispatch();

  const projectTypeReferential = useAppSelector(selectReferentialDataByKey(ReferentialKey.ProjectType))
  const referentialLoading = useAppSelector((state: RootState) => state.referential.loading)
  const insuranceProjectReferential = useAppSelector(selectReferentialDataByKey(ReferentialKey.InsuranceProject))
  const [isLoading, setIsLoading] = useState(false)

  const baseHook = useGenericProfiling();

  useEffect(() => {
    setIsLoading(referentialLoading)
  }, [referentialLoading])

  const borrowerQuestion = useMemo(() => {
    if (!projectTypeReferential || !insuranceProjectReferential) {
      return
    }
    if (projectTypeReferential.length === 0 || insuranceProjectReferential.length === 0) {
      return
    }
    return [
      {
        step: 1,
        questions: [
          {
            key: "projectType",
            label: "Décrivez-nous votre projet de crédit",
            isMultipleChoice: false,
            possibleAnswers: [
              ...projectTypeReferential
            ]
          }
        ]
      },
      {
        step: 2,
        questions: [
          {
            key: "insuranceProject",
            label: "Quelle situation correspond à votre projet d'assurance emprunteur ?",
            isMultipleChoice: false,
            possibleAnswers: [
              ...insuranceProjectReferential
            ]
          }
        ]
      },
      {
        step: 3,
        questions: [
          {
            key: "projectStartDate",
            label: "Quelle est la date prévisionnelle d'effet des garanties emprunteur ?",
            isMultipleChoice: false,
            possibleAnswers: [
              {
                type: 'input',
                config: {
                  type: 'date',
                  defaultValue: getTomorrowDate(),
                },
                validationRules: [
                  {
                    type: 'required',
                    message: 'La date est requise'
                  },
                  {
                    type: 'dateAfterToday',
                    message: 'La date doit être postérieure à aujourd\'hui'
                  },
                  {
                    type: 'dateWithinMonths',
                    value: 6,
                    message: 'La date doit être dans les 6 prochains mois'
                  }
                ]
              } as InputAnswer
            ]
          }
        ]
      }
    ]
  }, [projectTypeReferential, insuranceProjectReferential])

  useEffect(() => {
    if (borrowerQuestion)
      dispatch(initProfilingConfig({
        questionsGroups: borrowerQuestion,
        mode: ProfilingMode.AutoAdvance
      }));
  }, [dispatch, borrowerQuestion]);

  const handleQuestionAnswer = useCallback((key: string, answer: PossibleAnswer[]) => {
    const answers = baseHook.answers as Record<string, (IconPossibleAnswer | string)[]>
    if (baseHook.isLastStep) {
      const projectType = (answers['projectType'][0] as IconPossibleAnswer).id
      const insuranceProject = (answers['insuranceProject'][0] as IconPossibleAnswer).id
      const projectStartDate = answer[0] as string
      if (projectType && insuranceProject) {
        upsertBorrowerProject(projectType, insuranceProject, projectStartDate)
      }
    } else {
      baseHook.handleQuestionAnswer(key, answer)
    }
  }, [baseHook, upsertBorrowerProject])

  return {
    ...baseHook,
    handleQuestionAnswer,
    isLoading
  };
};
